import { ComponentType, ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arraySwap, rectSwappingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Interpolation, Theme } from '@emotion/react';
import {
  GetPresenceStatusResponse,
  TeamPresenceEntity_Type,
  UserPresence_UserStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone/presence/v1/api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  DeviceConnectionForPresenceEvent,
  DevicePresenceUpdateEvent,
  DeviceStatus,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/presence/message.pb';
import dayjs from 'dayjs';
import { debounce } from 'lodash-es';
import { useMutation } from 'react-query';
import { TeamPresenceV1 as TeamPresenceApi, SchemaV1Service as TeamPresenceService } from '@frontend/api-team-presence';
import { getUser } from '@frontend/auth-helpers';
import { BadgeBanner } from '@frontend/components';
import { useClickToCallModalTrigger, useDialpadTrigger } from '@frontend/contact-actions-place-call';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { TeamPresenceDemoBanner as TeamPresenceDemoBannerImage } from '@frontend/phones-images#image';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { useTeamChatSelector } from '@frontend/team-chat-new';
import { pendo } from '@frontend/tracking';
import { useUserPresence } from '@frontend/userpresence';
import { useWebsocketEventSubscription } from '@frontend/websocket';
import { theme } from '@frontend/theme';
import {
  Button,
  DropdownChip,
  DropdownFilterChipField,
  emptyStateGraphics,
  ModalControlModalProps,
  styles,
  TableFilters,
  Text,
  useAlert,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { useActiveLocationsMap } from '../../hooks/use-active-location-map';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { CheckListSelector } from '../filter-selectors/check-list-selector';
import { LocationsSelector } from '../filter-selectors/locations-checklist-selectors';
import { ManageCardButton } from './add-card';
import { CardSearch } from './atoms';
import {
  buildPresenceSubscriptionPayload,
  filterAndSortCardData,
  getPresenceSubscriptionSignature,
  reconcilePresenceWithUserStatusUpdates,
} from './card-data-layer.helpers';
import {
  CARD_TYPE_FILTER_TRAY_OPTIONS,
  CARD_TYPE_OPTIONS,
  DEVICE_STATE_FILTER_TRAY_OPTIONS,
  DEVICE_STATE_OPTIONS,
  SORT_OPTIONS,
  USER_STATE_FILTER_TRAY_OPTIONS,
  USER_STATE_OPTIONS,
} from './const';
import { DeviceCard } from './device-card';
import { useCardFiltersState, useStoredEntities, useTeamPresenceLocationIds } from './hooks';
import { cleanupDeviceUpdates, mergePresenceData } from './presence-merger';
import { TourHighlight } from './tour-highlight';
import { useTourHighlight } from './tour-highlight-context';
import {
  CardData,
  CardDataLayerProps,
  CardFilters,
  CardType,
  DEVICE_STATE,
  DeviceState,
  StoredEntity,
  UserState,
} from './types';
import { UserCard } from './user-card';

/**
 * Transforms presence status query response users to UserCardProps format
 */
export const transformPresenceUsersToCardData = ({
  presenceUsers = [],
  getScopeName,
  entityMap,
  orgId,
  canMessage,
  currentUserId,
}: {
  presenceUsers: GetPresenceStatusResponse['userPresence']; // Replace with actual type from GetPresenceStatusIO['output']
  getScopeName: (id: string) => string | undefined;
  entityMap: Record<string, StoredEntity>;
  orgId: string;
  canMessage: boolean;
  currentUserId?: string;
}) => {
  return presenceUsers.map((presence) => {
    const user = presence.userPresence;
    const device = presence.devicePresence;

    if (!user) {
      throw new Error('User presence data is missing');
    }

    const locationsIds = user && entityMap[user?.userId ?? ''] ? entityMap[user.userId ?? ''].locationIds : [];
    const locations = locationsIds.map((id) => {
      return { id, name: getScopeName(id) ?? '' };
    });

    return {
      id: user.userId ?? '',
      addedAt: entityMap[user.userId ?? ''] ? entityMap[user.userId ?? ''].addedAt : new Date().toISOString(),
      type: 'user' as const,
      name: user.userName.trim() ?? '',
      isOnline: user.userStatus === UserPresence_UserStatus.USER_PRESENCE_ONLINE,
      locations,
      orgId,
      deviceState: getDeviceState(device?.deviceStatus, device?.deviceType, user.userStatus),
      deviceName: device?.deviceName ?? '',
      deviceExtension: device?.deviceExtension ?? '',
      deviceType: device?.deviceType,
      deviceActivity: device
        ? {
            state: device.ongoingCallStartTimestamp ? ('active' as const) : ('idle' as const),
            timestamp: device.ongoingCallStartTimestamp ?? device.lastCallEndTimestamp,
          }
        : undefined,
      capabilities: {
        canCall: !!device,
        canMessage,
        hasTeamChatAccess: canMessage,
        canRemove: true,
        isSelf: user.userId === currentUserId,
      },
    };
  }) satisfies CardData[];
};

const deviceStateMap: Partial<Record<DeviceStatus, (typeof DEVICE_STATE)[keyof typeof DEVICE_STATE]>> = {
  [DeviceStatus.DEVICE_STATUS_UNKNOWN]: DEVICE_STATE.DISCONNECTED,
  [DeviceStatus.DEVICE_STATUS_AVAILABLE]: DEVICE_STATE.AVAILABLE,
  [DeviceStatus.DEVICE_STATUS_OFFLINE]: DEVICE_STATE.OFFLINE,
  [DeviceStatus.DEVICE_STATUS_ONCALL]: DEVICE_STATE.OCCUPIED,
  [DeviceStatus.DEVICE_STATUS_RINGING]: DEVICE_STATE.RINGING,
};

const getDeviceState = (
  status: DeviceStatus | undefined,
  deviceType?: string,
  userStatus?: UserPresence_UserStatus
) => {
  const base = deviceStateMap[status ?? DeviceStatus.DEVICE_STATUS_UNKNOWN] ?? DEVICE_STATE.DISCONNECTED;
  if (deviceType === 'softphone') {
    if (base === DEVICE_STATE.OFFLINE) return DEVICE_STATE.SOFTPHONE_OFFLINE;
    // A softphone that reports AVAILABLE while the user is offline/unknown is not actually usable
    if (
      base === DEVICE_STATE.AVAILABLE &&
      userStatus !== undefined &&
      userStatus !== UserPresence_UserStatus.USER_PRESENCE_ONLINE
    ) {
      return DEVICE_STATE.SOFTPHONE_OFFLINE;
    }
  }
  return base;
};

/**
 * Transforms presence status query response devices to DeviceCardProps format
 */
export const transformPresenceDevicesToCardData = ({
  presenceDevices = [],
  getScopeName,
  entityMap,
  orgId,
  canMessage,
  currentUserId,
}: {
  presenceDevices: GetPresenceStatusResponse['devicePresence']; // Replace with actual type from GetPresenceStatusIO['output']
  getScopeName: (id: string) => string | undefined;
  entityMap: Record<string, StoredEntity>;
  orgId: string;
  canMessage: boolean;
  currentUserId?: string;
}) => {
  return presenceDevices.map((presence) => {
    const user = presence.userPresence;
    const device = presence.devicePresence;

    if (!device) {
      throw new Error('Device presence data is missing');
    }

    const locationsIds = entityMap[device.sipProfileId ?? ''] ? entityMap[device.sipProfileId ?? ''].locationIds : [];
    const locations = locationsIds.map((id) => {
      return { id, name: getScopeName(id) ?? '' };
    });

    return {
      id: device.sipProfileId ?? '',
      userId: user?.userId,
      addedAt: entityMap[device.sipProfileId ?? '']
        ? entityMap[device.sipProfileId ?? ''].addedAt
        : new Date().toISOString(),
      type: 'device' as const,
      name: device.deviceName.trim() ?? '',
      subtitle: device.deviceExtension
        ? i18next.t(`Device • Ext. ${device.deviceExtension}`, { ns: 'phone' })
        : i18next.t('Device', { ns: 'phone' }),
      locations,
      orgId,
      deviceState: getDeviceState(device.deviceStatus, device.deviceType),
      deviceExtension: device.deviceExtension ?? '',
      deviceType: device.deviceType,
      userName: user?.userName ?? '',
      deviceActivity: device
        ? {
            state: device.ongoingCallStartTimestamp ? ('active' as const) : ('idle' as const),
            timestamp: device.ongoingCallStartTimestamp ?? device.lastCallEndTimestamp,
          }
        : undefined,
      capabilities: {
        canCall: true,
        canMessage: !!user && canMessage,
        hasTeamChatAccess: canMessage,
        canRemove: true,
        isSelf: user?.userId === currentUserId,
      },
    };
  }) satisfies CardData[];
};

/**
 * Reconciles merged presence with user-status updates, then maps it into flat card rows.
 * This is the single "data shaping" step used by CardDataLayer before filtering/sorting.
 */
const buildCardData = ({
  mergedPresenceData,
  userPresenceData,
  getScopeName,
  entityMap,
  orgId,
  canMessage,
  currentUserId,
}: {
  mergedPresenceData: {
    userPresence: GetPresenceStatusResponse['userPresence'];
    devicePresence: GetPresenceStatusResponse['devicePresence'];
  };
  userPresenceData: Record<string, { userStatus: string; lastSeen: string }>;
  getScopeName: (id: string) => string | undefined;
  entityMap: Record<string, StoredEntity>;
  orgId: string;
  canMessage: boolean;
  currentUserId?: string;
}) => {
  const { updatedUserPresence, updatedDevicePresence } = reconcilePresenceWithUserStatusUpdates({
    mergedPresenceData,
    userPresenceData,
  });

  const userData = transformPresenceUsersToCardData({
    presenceUsers: updatedUserPresence,
    getScopeName,
    entityMap,
    orgId,
    canMessage,
    currentUserId,
  });
  const deviceData = transformPresenceDevicesToCardData({
    presenceDevices: updatedDevicePresence,
    getScopeName,
    entityMap,
    orgId,
    canMessage,
    currentUserId,
  });

  return [...userData, ...deviceData];
};

/**
 * CardDataLayer pipeline:
 * 1) query + websocket updates are merged into a single presence snapshot
 * 2) presence is reconciled with user-status updates and mapped into card rows
 * 3) rows are filtered/sorted for UI rendering
 * 4) visible rows are converted into a stable subscription payload for heartbeat refresh
 */
export const CardDataLayer = ({ filters, render }: CardDataLayerProps) => {
  const { getScopeName, selectedOrgId } = useAppScopeStore();
  const teamPresenceLocationIds = useTeamPresenceLocationIds();
  const { entities } = useStoredEntities();
  const userPresenceStore = useUserPresence();
  const deferredFilters = useDeferredValue(filters);

  // Use refs to accumulate multiple rapid device presence updates
  const devicePresenceUpdatesRef = useRef<Map<string, DevicePresenceUpdateEvent>>(new Map());
  const [devicePresenceUpdateTrigger, setDevicePresenceUpdateTrigger] = useState(0);
  const [connectionUpdate, setConnectionUpdate] = useState<DeviceConnectionForPresenceEvent>();

  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const currentUser = getUser();

  const canChat = currentUser?.type !== 'weave' && getCustomizationFlagValue('chat');

  const { data: presenceData, status: queryStatus } = TeamPresenceApi.Queries.useGetPresenceStatusQuery({
    request: {
      sipProfileIds: entities
        .filter((e) => e.type === TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_DEVICE)
        .map((e) => e.id),
      userIds: entities.filter((e) => e.type === TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_USER).map((e) => e.id),
    },
    options: {
      enabled: entities.length > 0,
    },
  });

  // Targeted query for connection updates
  const { mutate: fetchTargetedPresence, data: targetedPresenceData } = useMutation({
    mutationFn: async (payload: { sipProfileIds?: string[]; userIds?: string[]; connected?: boolean }) => {
      const res = await TeamPresenceService.GetPresenceStatus(payload);

      return { ...res, connected: payload.connected };
    },
  });
  const entityMap = useMemo(() => {
    const map: Record<string, StoredEntity> = {};
    entities.forEach((entity) => {
      map[entity.id] = entity;
    });
    return map;
  }, [entities]);

  // Clean up websocket updates when presenceData changes (means server has newer data)
  useEffect(() => {
    if (presenceData && devicePresenceUpdatesRef.current.size > 0) {
      const cleaned = cleanupDeviceUpdates(
        { userPresence: presenceData.userPresence || [], devicePresence: presenceData.devicePresence || [] },
        devicePresenceUpdatesRef.current
      );
      devicePresenceUpdatesRef.current = cleaned;
    }
  }, [presenceData]);

  // Merge targeted presence data into main presence data
  const mergedPresenceData = useMemo(() => {
    if (!presenceData)
      return {
        userPresence: [],
        devicePresence: [],
      };

    return mergePresenceData({
      basePresence: {
        userPresence: presenceData.userPresence || [],
        devicePresence: presenceData.devicePresence || [],
      },
      targetedPresence: targetedPresenceData
        ? {
            userPresence: targetedPresenceData.userPresence || [],
            devicePresence: targetedPresenceData.devicePresence || [],
          }
        : undefined,
      deviceUpdates: devicePresenceUpdatesRef.current,
      enableLogging: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceData, targetedPresenceData, devicePresenceUpdateTrigger]);

  useWebsocketEventSubscription(NotificationType.DEVICE_CONNECTION_PRESENCE_UPDATE, (payload) => {
    setConnectionUpdate(payload.params);
  });

  useWebsocketEventSubscription(NotificationType.DEVICE_PRESENCE_UPDATE, (payload) => {
    const update = payload.params;
    if (update.sipProfileId) {
      console.log('[DevicePresence] Websocket update received:', {
        sipProfileId: update.sipProfileId,
        deviceStatus: update.deviceStatus,
        eventTimestamp: update.eventTimestamp,
        timestamp: new Date().toISOString(),
      });

      const existingUpdate = devicePresenceUpdatesRef.current.get(update.sipProfileId) as
        | DevicePresenceUpdateEvent
        | undefined;
      // Backend updates can arrive out of order; ignore stale events to prevent race-condition
      // regressions. If the incoming event timestamp is older than the last applied update for
      // this device, skip it.
      if (
        existingUpdate &&
        existingUpdate.eventTimestamp &&
        update.eventTimestamp &&
        update.eventTimestamp < existingUpdate.eventTimestamp
      ) {
        return;
      }

      // Add to the queue (or update if already exists for this sipProfileId)
      devicePresenceUpdatesRef.current.set(update.sipProfileId, update);

      // Trigger a single re-render to process all queued updates
      setDevicePresenceUpdateTrigger((prev) => prev + 1);
    }
  });

  // Fetch targeted presence data when connection update arrives
  useEffect(() => {
    if (!connectionUpdate) return;

    const payload: { sipProfileIds?: string[]; userIds?: string[]; connected?: boolean } = {
      connected: connectionUpdate.connected,
    };
    if (connectionUpdate.sipProfileId) {
      payload.sipProfileIds = [connectionUpdate.sipProfileId];
    }
    if (connectionUpdate.userId) {
      payload.userIds = [connectionUpdate.userId];
    }

    if (payload.sipProfileIds || payload.userIds) {
      fetchTargetedPresence(payload);
    }
  }, [connectionUpdate, fetchTargetedPresence]);

  const prevDataRef = useRef<CardData[]>([]);

  const data = useMemo(() => {
    if (entities.length === 0) {
      prevDataRef.current = [];
      return [];
    }

    if (queryStatus !== 'success' && queryStatus !== 'idle') {
      return prevDataRef.current;
    }

    const nextData = buildCardData({
      mergedPresenceData,
      userPresenceData: userPresenceStore.userPresenceData,
      getScopeName,
      entityMap,
      orgId: selectedOrgId,
      canMessage: canChat,
      currentUserId: currentUser?.userID,
    });
    prevDataRef.current = nextData;
    return nextData;
  }, [
    entities.length,
    mergedPresenceData,
    userPresenceStore.userPresenceData,
    getScopeName,
    entityMap,
    selectedOrgId,
    canChat,
    currentUser?.userID,
    queryStatus,
  ]);

  const locationList = useMemo(() => {
    return teamPresenceLocationIds.map((locationId) => ({
      value: locationId,
      label: getScopeName(locationId) || '',
    }));
  }, [teamPresenceLocationIds, getScopeName]);

  const filteredList = useMemo(() => {
    return filterAndSortCardData(data, deferredFilters);
  }, [data, deferredFilters]);

  const props = useMemo(() => ({ filteredList, locationList }), [filteredList, locationList]);

  // Only recalculate payload when the set of IDs changes, not when properties change
  const prevIdsRef = useRef<string>('');
  const prevPayloadRef = useRef<{ sipProfileIds: string[]; userIds: string[] } | null>(null);

  const subscriptionPayload = useMemo(() => {
    if (queryStatus !== 'success') return null;

    const currentIds = getPresenceSubscriptionSignature(filteredList);

    if (currentIds === prevIdsRef.current && prevPayloadRef.current) {
      return prevPayloadRef.current;
    }

    prevIdsRef.current = currentIds;
    const newPayload = buildPresenceSubscriptionPayload(filteredList);
    prevPayloadRef.current = newPayload;
    return newPayload;
  }, [filteredList, queryStatus]);

  const { mutate } = useMutation({
    mutationFn: (payload: { sipProfileIds: string[]; userIds: string[] }) =>
      TeamPresenceService.SubscribeToPresence(payload),
  });

  useEffect(() => {
    if (!subscriptionPayload) return;
    mutate(subscriptionPayload);
    // subscribe every 10 mins
    const interval = setInterval(() => {
      mutate(subscriptionPayload);
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [subscriptionPayload, mutate]);

  return render(props);
};

const DEMO_LOCATION_DENVER = { id: 'demo-denver', name: 'Denver' };
const DEMO_LOCATION_CHICAGO = { id: 'demo-chicago', name: 'Chicago' };
const DEMO_ORG_ID = 'team-presence-demo-org';
const DEMO_ENTITY_KEY = 'team-presence-demo';
const demoCards: CardData[] = [
  {
    id: 'demo-user-mary',
    addedAt: dayjs().subtract(1, 'day').toISOString(),
    type: 'user',
    name: 'Mary Demo',
    subtitle: 'Demo User',
    isOnline: true,
    locations: [DEMO_LOCATION_DENVER],
    orgId: DEMO_ORG_ID,
    deviceState: DEVICE_STATE.AVAILABLE,
    deviceName: 'DECT 400',
    deviceExtension: '400',
    deviceActivity: {
      state: 'idle',
      timestamp: dayjs().subtract(42, 'minute').toISOString(),
    },
    capabilities: {
      canCall: true,
      canMessage: true,
      canRemove: true,
    },
  },
  {
    id: 'demo-device-phone',
    userId: 'demo-user-john',
    addedAt: dayjs().subtract(2, 'day').toISOString(),
    type: 'device',
    name: 'Demo Phone',
    subtitle: i18next.t('Demo Device', { ns: 'phone' }),
    locations: [DEMO_LOCATION_CHICAGO],
    orgId: DEMO_ORG_ID,
    deviceState: DEVICE_STATE.OCCUPIED,
    deviceExtension: '400',
    userName: 'John Ashley',
    deviceActivity: {
      state: 'active',
      timestamp: dayjs().subtract(3, 'minute').toISOString(),
    },
    capabilities: {
      canCall: true,
      canMessage: true,
      canRemove: true,
    },
  },
];

export const DemoCardDataLayer = ({ filters, render }: CardDataLayerProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const deferredFilters = useDeferredValue(filters);
  const {
    entities: demoStoredEntities,
    addEntities: addDemoEntities,
    hasEntityKey: hasDemoEntityKey,
  } = useStoredEntities({ entityKeyOverride: DEMO_ENTITY_KEY });
  useEffect(() => {
    if (hasDemoEntityKey || !demoCards.length) return;
    addDemoEntities(
      demoCards.map((card) => ({
        id: card.id,
        type:
          card.type === 'user'
            ? TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_USER
            : TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_DEVICE,
        locationIds: [],
      }))
    );
  }, [addDemoEntities, hasDemoEntityKey]);

  const activeDemoIds = useMemo(() => {
    if (!hasDemoEntityKey) return null;
    return new Set(demoStoredEntities.map((entity) => entity.id));
  }, [demoStoredEntities, hasDemoEntityKey]);

  const isMultiLocationSite = selectedLocationIds.length > 1;

  const filteredList = useMemo(() => {
    if (!demoCards.length) return [];

    const sourceCards = activeDemoIds ? demoCards.filter((item) => activeDemoIds.has(item.id)) : demoCards;
    return filterAndSortCardData(sourceCards, {
      ...deferredFilters,
      location: isMultiLocationSite ? deferredFilters.location : [],
    });
  }, [activeDemoIds, deferredFilters, isMultiLocationSite]);
  const locationList = useMemo(
    () =>
      isMultiLocationSite
        ? [
            { value: DEMO_LOCATION_DENVER.id, label: DEMO_LOCATION_DENVER.name },
            { value: DEMO_LOCATION_CHICAGO.id, label: DEMO_LOCATION_CHICAGO.name },
          ]
        : [],
    [isMultiLocationSite]
  );

  return render({ filteredList, locationList, showBanner: true });
};

const CHUNK_SIZE = 10;

export const CardGrid = <T extends { id: string; type: 'user' | 'device' }>({
  cellWidth = 326,
  list,
  renderer,
  disableDrag = false,
}: {
  cellWidth?: number;
  list: T[];
  renderer: (item: T) => ReactNode;
  disableDrag?: boolean;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: disableDrag ? { distance: Infinity } : undefined,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [visibleChunks, setVisibleChunks] = useState(1); // Start with first chunk

  // Derive the actual order - use local order if it exists and is valid, otherwise use list order
  const order = useMemo(() => {
    const listIds = list.map((item) => item.id);

    // If drag is disabled or we don't have a local order, use natural list order
    if (disableDrag || localOrder.length === 0) {
      return listIds;
    }

    // If we have a local order and it contains all current list items, use it
    if (
      localOrder.length === listIds.length &&
      listIds.every((id) => localOrder.includes(id)) &&
      localOrder.every((id) => listIds.includes(id))
    ) {
      return localOrder;
    }

    // Otherwise, use the natural list order and clear local order
    return listIds;
  }, [list, localOrder, disableDrag]);

  function handleDragStart(event: DragStartEvent) {
    if (disableDrag) return;
    const { active } = event;

    setActiveId(active.id.toString());
  }

  function handleDragEnd(event: DragEndEvent) {
    if (disableDrag) {
      setActiveId(null);
      return;
    }

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const newOrder = [...order];
      const oldIndex = newOrder.indexOf(active.id.toString());
      const newIndex = newOrder.indexOf(over.id.toString());
      const reorderedItems = arraySwap(newOrder, oldIndex, newIndex);

      setLocalOrder(reorderedItems);
    }

    setActiveId(null);
  }

  const sortedItems = useMemo(() => {
    const itemMap = new Map(list.map((item) => [item.id, item]));
    return order.map((id) => itemMap.get(id)).filter((item): item is T => item !== undefined);
  }, [list, order]);

  const chunkedList = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < sortedItems.length; i += CHUNK_SIZE) {
      chunks.push(sortedItems.slice(i, i + CHUNK_SIZE));
    }
    return chunks;
  }, [sortedItems]);

  const visibleChunkedItems = useMemo(() => {
    return chunkedList.slice(0, visibleChunks).flat();
  }, [chunkedList, visibleChunks]);

  const prevListRef = useRef<{ ids: Set<string>; length: number }>({ ids: new Set(), length: 0 });

  useEffect(() => {
    const currentIds = new Set(list.map((item) => item.id));
    const prevIds = prevListRef.current.ids;

    // Only reset if:
    // 1. List became significantly smaller (items were removed)
    // 2. The set of IDs changed (different items, not just reordered)
    const lengthDecreasedSignificantly = list.length < prevListRef.current.length - CHUNK_SIZE;
    const idsChanged = currentIds.size !== prevIds.size || Array.from(currentIds).some((id) => !prevIds.has(id));

    if (lengthDecreasedSignificantly || idsChanged) {
      setVisibleChunks(1);
    }

    prevListRef.current = { ids: currentIds, length: list.length };
  }, [list]);

  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visibleChunks >= chunkedList.length) {
      return; // All chunks visible, no need to observe
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleChunks((prev) => Math.min(prev + 1, chunkedList.length));
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '200px', // Load earlier to reduce perceived latency
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [visibleChunks, chunkedList.length]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={order} strategy={rectSwappingStrategy}>
        <div
          css={{
            display: 'grid',
            padding: 1,
            gridTemplateColumns: `repeat(auto-fill, minmax(${cellWidth}px, 1fr))`,
            gap: theme.spacing(2),
            width: '100%',
            overflowY: 'auto',

            '@container (max-width: 500px)': {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          {visibleChunkedItems.map((item) => (
            <div key={item.id}>{renderer(item)}</div>
          ))}

          {/* Sentinel element to trigger loading */}
          {visibleChunks < chunkedList.length && <div ref={observerRef} css={{ gridColumn: '1 / -1', height: 1 }} />}
        </div>
        <DragOverlay
          style={{
            borderRadius: theme.borderRadius.medium,
            boxShadow: theme.shadows.floating,
            zIndex: theme.zIndex.high,
          }}
        >
          {activeId ? renderer(list.find((item) => item.id === activeId) as T) : null}
        </DragOverlay>
      </SortableContext>
    </DndContext>
  );
};

export const CardDataFilters = ({
  filters,
  locationList,
  handleFilterChange,
  handleBulkFilterChange,
}: {
  filters: CardFilters;
  locationList: { label: string; value: string }[];
  handleFilterChange: <K extends keyof CardFilters>(field: K, value: CardFilters[K]) => void;
  handleBulkFilterChange: (updates: Partial<CardFilters>) => void;
}) => {
  const { t } = useTranslation('phone');
  const { triggerProps, modalProps } = useModalControl();

  const debouncedSearchChange = debounce((value) => {
    handleFilterChange('search', value);
  }, 300);

  return (
    <div
      css={{
        containerType: 'inline-size',
        containerName: 'card-filter-container',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateAreas: '"filters . search tray-trigger"',
          gridTemplateColumns: 'auto 1fr auto auto',
        }}
      >
        <div
          css={{
            display: 'grid',
            gap: theme.spacing(2),
            gridAutoFlow: 'column',
            alignItems: 'center',
            gridArea: 'filters',
          }}
        >
          <LocationFilterMenu
            options={locationList}
            onChange={(location) => {
              handleFilterChange('location', location);
            }}
            showClearAll={false}
            selectedOptions={filters.location}
            trackingIdBase={teamPresenceTrackingId({ context: 'filter-bar::location-filter' })}
          />
          <TourHighlight
            dotId='filter-device-state'
            message={t('Filter your Users and Devices by phone status.')}
            placement='top'
            containerCss={{ width: 'auto', display: 'inline-flex' }}
          >
            <StatusFilterMenu
              selectedDeviceOptions={filters.deviceState}
              selectedUserOptions={filters.userStatus}
              onChange={({ deviceState, userStatus }) => {
                handleBulkFilterChange({ deviceState, userStatus });
              }}
              trackingId={teamPresenceTrackingId({ context: 'filter-bar::status-filter' })}
            />
          </TourHighlight>
          <TourHighlight
            dotId='filter-card-type'
            message={t('Filter by type, user or device')}
            placement='top'
            containerCss={{ display: 'inline-flex' }}
          >
            <FilterMenu
              options={CARD_TYPE_OPTIONS}
              onChange={(type) => {
                handleFilterChange('cardType', type as CardType[]);
              }}
              showClearAll={false}
              itemLabel={t('Type')}
              itemCountLabel={(count) => i18next.t(`{{count}} types`, { count })}
              fieldName='card-type-filter'
              selectedOptions={filters.cardType}
              trackingId={teamPresenceTrackingId({ context: 'filter-bar::card-type-filter' })}
            />
          </TourHighlight>
          {/* <FilterMenu
            options={SORT_OPTIONS}
            onChange={(statuses) => {
              handleFilterChange('userStatus', statuses);
            }}
            showClearAll={false}
            itemLabel={t('User Status')}
            itemCountLabel={(count) => i18next.t(`{{count}} Statuses`, { count })}
            fieldName='user-status-filter'
          /> */}
          <SortMenu
            onChange={(opt) => {
              handleFilterChange('sortBy', opt);
            }}
            labelPrefix={t('Sort By: ')}
            options={SORT_OPTIONS}
            selectedOption={SORT_OPTIONS.find((opt) => opt.value === filters.sortBy) || SORT_OPTIONS[0]}
            trackingId={teamPresenceTrackingId({ context: 'filter-bar::sort-by' })}
          />
        </div>
        <div />
        <div
          css={{
            gridArea: 'search',
          }}
        >
          <CardSearch value={filters.search} onChange={debouncedSearchChange} />
        </div>
        <div css={{ gridArea: 'tray-trigger' }}>
          <CardFilterTray
            filters={filters}
            triggerProps={triggerProps}
            modalProps={modalProps}
            handleBulkFilterChange={handleBulkFilterChange}
            locationList={locationList}
          />
        </div>
      </div>
    </div>
  );
};

export const CardDataFiltersCompact = ({
  filters,
  locationList,
  handleFilterChange,
  handleBulkFilterChange,
}: {
  filters: CardFilters;
  locationList: { label: string; value: string }[];
  handleFilterChange: <K extends keyof CardFilters>(field: K, value: CardFilters[K]) => void;
  handleBulkFilterChange: (updates: Partial<CardFilters>) => void;
}) => {
  const { t } = useTranslation('phone');
  const { triggerProps, modalProps } = useModalControl();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const debouncedSearchChange = debounce((value) => {
    handleFilterChange('search', value);
  }, 300);

  return (
    <div
      css={{
        containerType: 'inline-size',
        containerName: 'card-filter-container',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: isSearchExpanded ? 0 : theme.spacing(2),
          gridTemplateAreas: isSearchExpanded ? '"controls"' : '"filters . controls"',
          gridTemplateColumns: isSearchExpanded ? 'auto' : 'auto 1fr auto',
        }}
      >
        {!isSearchExpanded && (
          <div
            css={{
              display: 'grid',
              gap: theme.spacing(2),
              gridAutoFlow: 'column',
              alignItems: 'center',
              gridArea: 'filters',
            }}
          >
            <SortMenu
              onChange={(opt) => {
                handleFilterChange('sortBy', opt);
              }}
              labelPrefix={t('Sort By: ')}
              options={SORT_OPTIONS}
              selectedOption={SORT_OPTIONS.find((opt) => opt.value === filters.sortBy) || SORT_OPTIONS[0]}
              trackingId={teamPresenceTrackingId({ context: 'filter-bar::sort-by' })}
            />
          </div>
        )}
        <div />
        <div
          css={{
            gridArea: 'controls',
            display: 'grid',
            gridAutoFlow: 'column',
            gap: theme.spacing(1),
          }}
        >
          <CardSearch
            value={filters.search}
            useCompactMode
            onChange={debouncedSearchChange}
            isExpanded={isSearchExpanded}
            onOpen={() => setIsSearchExpanded(true)}
            onDismiss={() => setIsSearchExpanded(false)}
          />
          {!isSearchExpanded && (
            <CardFilterTray
              filters={filters}
              triggerProps={triggerProps}
              modalProps={modalProps}
              handleBulkFilterChange={handleBulkFilterChange}
              locationList={locationList}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const hasFiltersApplied = (filters: CardFilters) => {
  return (
    filters.location.length > 0 ||
    filters.deviceState.length > 0 ||
    filters.cardType.length > 0 ||
    filters.userStatus.length > 0
  );
};

const CardFilterTray = ({
  filters,
  triggerProps,
  modalProps,
  handleBulkFilterChange,
  locationList,
}: {
  filters: CardFilters;
  triggerProps: any;
  modalProps: ModalControlModalProps;
  handleBulkFilterChange: (updates: Partial<CardFilters>) => void;
  locationList?: { label: string; value: string }[];
}) => {
  const { t } = useTranslation('phone');
  const activeLocationsMap = useActiveLocationsMap();
  const locationListMap = useMemo(() => {
    if (!locationList || locationList.length === 0) return null;
    return locationList.reduce((acc, location) => {
      acc[location.value] = location.label;
      return acc;
    }, {} as Record<string, string>);
  }, [locationList]);
  const allLocationsIdToNameMap = locationListMap ?? activeLocationsMap;

  const [localFilters, setLocalFilters] = useState<
    Pick<CardFilters, 'location' | 'deviceState' | 'cardType' | 'userStatus'>
  >({
    location: filters.location,
    deviceState: filters.deviceState,
    cardType: filters.cardType,
    userStatus: filters.userStatus,
  });

  // Sync local filters when modal opens
  useEffect(() => {
    if (modalProps.show) {
      setLocalFilters({
        location: filters.location,
        deviceState: filters.deviceState,
        cardType: filters.cardType,
        userStatus: filters.userStatus,
      });
    }
  }, [modalProps.show, filters.location, filters.deviceState, filters.cardType, filters.userStatus]);

  const handleLocalFilterChange = <K extends keyof typeof localFilters>(field: K, value: (typeof localFilters)[K]) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    handleBulkFilterChange(localFilters);
    pendo.track(teamPresenceTrackingId({ context: 'filter-tray::apply-event' }), {
      locationFiltersCount: localFilters.location.length,
      deviceStateFiltersCount: localFilters.deviceState.length,
      cardTypeFiltersCount: localFilters.cardType.length,
      userStatusFiltersCount: localFilters.userStatus.length,
    });
    modalProps.onClose?.();
  };

  const handleClearAll = () => {
    const clearedFilters = {
      location: [],
      deviceState: [],
      cardType: [],
      userStatus: [],
    };
    setLocalFilters(clearedFilters);
    handleBulkFilterChange(clearedFilters);
  };

  return (
    <>
      <Button
        variant='secondary'
        size='large'
        iconName='filter-alt'
        {...triggerProps}
        trackingId={teamPresenceTrackingId({ context: 'filter-bar::filter-tray-trigger' })}
        css={{
          '::after': {
            position: 'absolute',
            background: theme.colors.primary50,
            borderRadius: 16,
            border: `1.5px solid ${theme.colors.white}`,
            height: theme.spacing(1.5),
            width: theme.spacing(1.5),
            top: 6,
            left: theme.spacing(3),
            zIndex: theme.zIndex.high,
            content: '""',
            transition: 'border-color, opacity ease-in-out 250ms',
            opacity: hasFiltersApplied(filters) ? 1 : 0,
          },
          ':hover::after, :focus::after': {
            borderColor: theme.colors.neutral10,
          },
        }}
      />
      <TableFilters
        {...modalProps}
        onApplyClick={handleApply}
        onClearAllClick={handleClearAll}
        disableApplyButton={false}
        trackingId={teamPresenceTrackingId({ context: 'filter-bar::filter-tray' })}
      >
        <TableFilters.Section
          sectionHeaderLabel={t('Device Status')}
          onClearClick={() => {
            handleLocalFilterChange('deviceState', []);
          }}
        >
          <CheckListSelector<DeviceState>
            label={''}
            onChange={(result) => {
              handleLocalFilterChange('deviceState', result);
            }}
            options={DEVICE_STATE_FILTER_TRAY_OPTIONS}
            showLabel={false}
            value={localFilters.deviceState}
            trackingIdBase='recent-calls-result-selector-table-filter'
          />
        </TableFilters.Section>

        <TableFilters.Section
          sectionHeaderLabel={t('User Status')}
          onClearClick={() => {
            handleLocalFilterChange('userStatus', []);
          }}
        >
          <CheckListSelector<UserState>
            label={''}
            onChange={(result) => {
              handleLocalFilterChange('userStatus', result);
            }}
            options={USER_STATE_FILTER_TRAY_OPTIONS}
            showLabel={false}
            value={localFilters.userStatus}
            trackingIdBase='recent-calls-result-selector-table-filter'
          />
        </TableFilters.Section>

        <TableFilters.Section
          sectionHeaderLabel={t('Type')}
          onClearClick={() => {
            handleLocalFilterChange('cardType', []);
          }}
        >
          <CheckListSelector<CardType>
            label={''}
            onChange={(result) => {
              handleLocalFilterChange('cardType', result);
            }}
            options={CARD_TYPE_FILTER_TRAY_OPTIONS}
            showLabel={false}
            value={localFilters.cardType}
            trackingIdBase='recent-calls-result-selector-table-filter'
          />
        </TableFilters.Section>

        {Object.keys(allLocationsIdToNameMap).length > 1 && (
          <TableFilters.Section
            sectionHeaderLabel={t('Location')}
            onClearClick={() => {
              handleLocalFilterChange('location', []);
            }}
          >
            <LocationsSelector
              allLocationsOption={allLocationsIdToNameMap}
              onChange={(selLocationIds) => {
                handleLocalFilterChange('location', selLocationIds);
              }}
              selectedLocationsValue={localFilters.location}
              trackingId='recent-calls-location-selector-table-filter'
            />
          </TableFilters.Section>
        )}
      </TableFilters>
    </>
  );
};

type DemoBannerTrackingIds = {
  addTeam?: string;
  startTour?: string;
  dismiss?: string;
};

type TeamPresenceDemoBannerProps = {
  onDismiss: () => void;
  trackingIds?: DemoBannerTrackingIds;
  initialLocationIds?: string[];
};

const TeamPresenceDemoBanner = ({ onDismiss, trackingIds = {}, initialLocationIds }: TeamPresenceDemoBannerProps) => {
  const { t } = useTranslation('phone');
  const { isTourActive, setIsTourActive } = useTourHighlight();

  return (
    <div
      css={{
        display: 'grid',
        width: '100%',
        height: 220,
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.secondary.seaweed40}`,
        boxShadow: theme.shadows.floating,
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          padding: theme.spacing(2, 3),
          height: 56,
          background: theme.colors.secondary.seaweed10,
          borderBottom: `1px solid ${theme.colors.secondary.seaweed30}`,
        }}
      >
        <BadgeBanner.AnimatedBadge key='new-badge' label={t('New')} />
        <Text
          as='h2'
          size='large'
          weight='bold'
          css={{ color: theme.colors.neutral90, fontSize: theme.font.size.h3, lineHeight: '24px' }}
        >
          {t('Welcome to Team Status')}
        </Text>
      </div>
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(2),
          padding: theme.spacing(2),
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          background: theme.colors.white,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: theme.spacing(2),
          }}
        >
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
              alignSelf: 'stretch',
            }}
          >
            <Text as='p' size='medium' weight='bold' css={{ color: theme.colors.neutral90 }}>
              {t("See who's available, on a call, or away at a glance.")}
            </Text>
            <Text as='p' size='small' css={{ color: theme.colors.neutral90 }}>
              {t('This demo shows you how to monitor Users and Devices in real-time.')}
            </Text>
          </div>
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.spacing(1),
              alignItems: 'center',
            }}
          >
            <ManageCardButton
              variant='cta'
              label={t('Add Your Team')}
              size='large'
              initialLocationIds={initialLocationIds}
            />
            <Button
              variant='tertiary'
              size='large'
              onClick={() => {
                setIsTourActive((prev) => !prev);
              }}
              trackingId={trackingIds.startTour}
              css={{
                color: theme.colors.neutral70,
                borderRadius: theme.borderRadius.small,
                border: `1px solid ${theme.colors.neutral30}`,
              }}
            >
              {isTourActive ? t('End Tour') : t('Start Tour')}
            </Button>
            <Button
              variant='tertiary'
              size='large'
              onClick={onDismiss}
              trackingId={teamPresenceTrackingId({ context: 'demo-banner::dismiss' })}
              css={{
                color: theme.colors.neutral60,
              }}
            >
              {t('Maybe later')}
            </Button>
          </div>
        </div>
        <div
          css={{
            width: 283,
            height: 128,
            borderRadius: theme.borderRadius.small,
            boxShadow: theme.shadows.floating,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            alignSelf: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <TeamPresenceDemoBannerImage
            alt={t('Team status preview')}
            objectFit='contain'
            containerStyle={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transformOrigin: 'left top',
            }}
            imageStyle={{
              objectPosition: 'left top',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const CardView = ({
  DataLayer,
  filters,
  setFilters,
  defaultFilters,
  isDemoDismissed = false,
  onDemoDismiss,
}: {
  DataLayer: ComponentType<CardDataLayerProps>;
  filters: CardFilters;
  setFilters: ReturnType<typeof useCardFiltersState>[1];
  defaultFilters: ReturnType<typeof useCardFiltersState>[2];
  isDemoDismissed?: boolean;
  onDemoDismiss?: () => void;
}) => {
  const { t } = useTranslation('phone');
  const { setIsTourActive } = useTourHighlight();

  const { isSmallMode } = useIsSmallMode();
  const { entityKey, removeEntity, undoRemoveEntity, entities, isPinned, entityExists, pinEntity, unpinEntity } =
    useStoredEntities();

  const {
    removeEntity: removeDemoEntity,
    undoRemoveEntity: undoRemoveDemoEntity,
    entities: demoEntities,
  } = useStoredEntities({
    entityKeyOverride: DEMO_ENTITY_KEY,
  });
  const hasAnyCards = isDemoDismissed ? entities.length > 0 : demoEntities.length > 0;
  // Team chat integration
  const openTeamChatWithUser = useTeamChatSelector((ctx) => ctx?.openConversationWithUser);
  const { triggerDialpad } = useDialpadTrigger();
  const { triggerClickToCallModal } = useClickToCallModalTrigger();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const alerts = useAlert();

  const handleMessageClick = useCallback(
    (userId: string, userName: string, _orgId: string) => {
      if (!isDemoDismissed) return;
      openTeamChatWithUser?.({ userId, firstName: userName });
    },
    [openTeamChatWithUser, isDemoDismissed]
  );

  const handleCallClick = useCallback(
    (deviceExtension: string) => {
      if (!isDemoDismissed) return;
      if (!!phoneConfig) {
        triggerDialpad(deviceExtension);
      } else {
        triggerClickToCallModal({
          dialNumber: deviceExtension,
          isLoading: false,
        });
      }
    },
    [isDemoDismissed, phoneConfig, triggerDialpad, triggerClickToCallModal]
  );

  const handleRemoveClick = useCallback(
    (id: string, name: string) => {
      const performRemove = isDemoDismissed ? removeEntity : removeDemoEntity;
      const performUndo = isDemoDismissed ? undoRemoveEntity : undoRemoveDemoEntity;

      try {
        performRemove(id);
        alerts.success({
          message: t('{{name}} removed successfully.', { name }),
          action: {
            label: t('Undo'),
            onClick: () => {
              performUndo();
            },
          },
        });
      } catch (error) {
        alerts.error({ message: t('Failed to remove {{name}}. Please try again.', { name }) });
      }
    },
    [removeEntity, alerts, t, undoRemoveEntity, isDemoDismissed, removeDemoEntity, undoRemoveDemoEntity]
  );

  const handleFilterChange = useCallback(
    <K extends keyof CardFilters>(field: K, value: CardFilters[K]) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    [setFilters]
  );

  const handleBulkFilterChange = useCallback(
    (updates: Partial<CardFilters>) => {
      setFilters((prev) => ({ ...prev, ...updates }));
    },
    [setFilters]
  );

  const handleDemoDismiss = useCallback(() => {
    if (isDemoDismissed) return;
    setIsTourActive(false);
    onDemoDismiss?.();
    handleBulkFilterChange(defaultFilters);
  }, [isDemoDismissed, onDemoDismiss, setIsTourActive, defaultFilters, handleBulkFilterChange]);

  return (
    <div css={{ display: 'grid', gap: theme.spacing(4) }}>
      <DataLayer
        filters={filters}
        render={({ filteredList, locationList, showBanner }) => {
          const showLocations = locationList.length > 1;

          // Memoized renderer - will only recreate if dependencies change
          const renderCard = (item: CardData, showUnpin = false) => {
            if (item.type === 'user') {
              return (
                <UserCard
                  {...item}
                  showLocations={showLocations}
                  onMessageClick={handleMessageClick}
                  onCallClick={handleCallClick}
                  onRemoveClick={handleRemoveClick}
                  onPinClick={(id) => (isPinned(id) ? unpinEntity(id) : pinEntity(id))}
                  isPinned={isPinned(item.id)}
                  showUnpinIcon={showUnpin}
                />
              );
            }

            return (
              <DeviceCard
                {...item}
                showLocations={showLocations}
                onMessageClick={handleMessageClick}
                onCallClick={handleCallClick}
                onRemoveClick={handleRemoveClick}
                onPinClick={(id) => (isPinned(id) ? unpinEntity(id) : pinEntity(id))}
                isPinned={isPinned(item.id)}
                showUnpinIcon={showUnpin}
              />
            );
          };

          const pinnedList = filteredList.filter((item) => entityExists(item.id) && isPinned(item.id));
          const unpinnedList = filteredList.filter((item) => entityExists(item.id) && !isPinned(item.id));
          const hasPinnedCards = pinnedList.length > 0;

          return (
            <>
              <div css={{ display: 'grid', gap: theme.spacing(2) }}>
                {showBanner ? (
                  <TeamPresenceDemoBanner
                    onDismiss={handleDemoDismiss}
                    initialLocationIds={filters.location}
                    trackingIds={{
                      addTeam: teamPresenceTrackingId({ context: 'demo-banner::add-team' }),
                      startTour: teamPresenceTrackingId({ context: 'demo-banner::start-tour' }),
                      dismiss: teamPresenceTrackingId({ context: 'demo-banner::dismiss' }),
                    }}
                  />
                ) : null}
                {isSmallMode ? (
                  <CardDataFiltersCompact
                    filters={filters}
                    locationList={locationList}
                    handleFilterChange={handleFilterChange}
                    handleBulkFilterChange={handleBulkFilterChange}
                  />
                ) : (
                  <CardDataFilters
                    filters={filters}
                    locationList={locationList}
                    handleFilterChange={handleFilterChange}
                    handleBulkFilterChange={handleBulkFilterChange}
                  />
                )}
                <div
                  css={{
                    containerType: 'inline-size',
                    containerName: 'card-grid-container',
                  }}
                >
                  {filteredList.length === 0 ? (
                    <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                      <div
                        css={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: theme.spacing(2),
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          padding: theme.spacing(3, 5),
                        }}
                      >
                        {hasAnyCards ? (
                          <>
                            <emptyStateGraphics.call_group css={{ maxWidth: 248, maxHeight: 248 }} />
                            <Text textAlign='center' color='light' size='medium'>
                              {t('No Users or Devices match your filters.')}
                            </Text>
                          </>
                        ) : (
                          <>
                            <emptyStateGraphics.search css={{ maxWidth: 200, maxHeight: 200 }} />
                            <Text textAlign='center' css={{ maxWidth: 450 }} color='light' size='large'>
                              {isSmallMode
                                ? t(
                                    'No users or devices yet. Click "Manage" to begin monitoring your team\'s phone activity in real time.'
                                  )
                                : t(
                                    'No Users or Devices yet. Click "Manage Users and Devices" to begin monitoring your team\'s phone activity in real time.'
                                  )}
                            </Text>
                            <ManageCardButton
                              key={entityKey}
                              variant='cta'
                              label={isSmallMode ? t('Manage') : t('Manage Users and Devices')}
                              initialLocationIds={filters.location}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ) : hasPinnedCards ? (
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                      {/* Pinned Section */}
                      <>
                        <div
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                          }}
                        >
                          <Icon name='pin-filled' size={16} />
                          <Text weight='bold'>{t('Pinned')}</Text>
                        </div>
                        <CardGrid
                          disableDrag={true}
                          key={`pinned-${filters.sortBy}`}
                          list={pinnedList}
                          renderer={(item) => renderCard(item, true)}
                        />
                      </>

                      {/* Other Section */}
                      {unpinnedList.length > 0 && (
                        <>
                          <div
                            css={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            <Icon name='contacts' size={16} color='default' />
                            <Text weight='bold'>{t('Other Users and Devices')}</Text>
                          </div>
                          <CardGrid
                            disableDrag={true}
                            key={`unpinned-${filters.sortBy}`}
                            list={unpinnedList}
                            renderer={renderCard}
                          />
                        </>
                      )}
                    </div>
                  ) : (
                    <CardGrid disableDrag={true} key={filters.sortBy} list={filteredList} renderer={renderCard} />
                  )}
                </div>
              </div>
              {showBanner && filteredList.length !== 0 ? (
                <ManageCardButton
                  variant='page'
                  label={t('Add Your First User or Device')}
                  initialLocationIds={filters.location}
                  css={{
                    color: theme.colors.neutral70,
                    borderRadius: theme.borderRadius.small,
                    border: `1px solid ${theme.colors.neutral30}`,
                    width: 'fit-content',
                    height: 40,
                  }}
                />
              ) : null}
            </>
          );
        }}
      />
    </div>
  );
};

type Option = {
  label: string;
  value: string;
};

const DEVICE_OPTION_PREFIX = 'device:';
const USER_OPTION_PREFIX = 'user:';

type StatusFilterMenuProps = {
  selectedDeviceOptions?: DeviceState[];
  selectedUserOptions?: UserState[];
  onChange: (update: { deviceState: DeviceState[]; userStatus: UserState[] }) => void;
  trackingId: string;
};

const StatusFilterMenu = ({
  selectedDeviceOptions,
  selectedUserOptions,
  onChange,
  trackingId,
}: StatusFilterMenuProps) => {
  const { t } = useTranslation('phone');

  const combinedSelected = useMemo(
    () => [
      ...(selectedDeviceOptions ?? []).map((v) => `${DEVICE_OPTION_PREFIX}${v}`),
      ...(selectedUserOptions ?? []).map((v) => `${USER_OPTION_PREFIX}${v}`),
    ],
    [selectedDeviceOptions, selectedUserOptions]
  );

  const checkListField = useFormField({ type: 'checklist', value: combinedSelected }, [combinedSelected]);

  const filterIsActive = checkListField.value && checkListField.value.length > 0;

  const handleChange = (values: string[]) => {
    const deviceState = values
      .filter((v) => v.startsWith(DEVICE_OPTION_PREFIX))
      .map((v) => v.slice(DEVICE_OPTION_PREFIX.length)) as DeviceState[];
    const userStatus = values
      .filter((v) => v.startsWith(USER_OPTION_PREFIX))
      .map((v) => v.slice(USER_OPTION_PREFIX.length)) as UserState[];
    onChange({ deviceState, userStatus });
  };

  return (
    <DropdownFilterChipField
      {...checkListField}
      filterIsActive={filterIsActive}
      onChange={(e) => {
        checkListField.onChange(e);
        // @ts-expect-error - field types need to be fixed
        handleChange(e.value);
      }}
      getCustomLabel={(items) => (items.length > 1 ? t(`{{count}} Statuses`, { count: items.length }) : items[0])}
      label={t('Status')}
      name='status-filter'
      showClearAll={false}
      trackingIdBase={trackingId}
      onApply={({ value, close }) => {
        pendo.track(trackingId + '::apply-event', { selected: value });
        close();
      }}
    >
      <DropdownFilterChipField.Group label={t('Device Status')}>
        {DEVICE_STATE_OPTIONS.map((option) => (
          <DropdownFilterChipField.Option
            key={option.value}
            value={`${DEVICE_OPTION_PREFIX}${option.value}`}
            css={{ label: styles.truncate }}
          >
            {option.label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField.Group>
      <DropdownFilterChipField.Group label={t('User Status')}>
        {USER_STATE_OPTIONS.map((option) => (
          <DropdownFilterChipField.Option
            key={option.value}
            value={`${USER_OPTION_PREFIX}${option.value}`}
            css={{ label: styles.truncate }}
          >
            {option.label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField.Group>
    </DropdownFilterChipField>
  );
};

type FilterMenuProps = {
  options: Option[];
  selectedOptions?: string[];
  onChange: (opts: string[]) => void;
  showClearAll?: boolean;
  className?: string;
  containerCss?: Interpolation<Theme>;
  itemLabel?: string;
  itemCountLabel?: (count: number) => string;
  fieldName: string;
  trackingId: string;
};

export const FilterMenu = ({
  options,
  selectedOptions,
  onChange,
  showClearAll,
  className,
  containerCss,
  itemLabel,
  itemCountLabel = (count) => i18next.t(`{{count}} Items`, { count }),
  fieldName,
  trackingId,
}: FilterMenuProps) => {
  const checkListField = useFormField(
    {
      type: 'checklist',
      value: selectedOptions,
    },
    [selectedOptions]
  );

  // Only give the user the option to filter by location if there are multiple locations to choose from
  if (options.length < 2) {
    return null;
  }

  const filterIsActive = checkListField.value && checkListField.value.length > 0;

  return (
    <div css={containerCss}>
      <DropdownFilterChipField
        {...checkListField}
        filterIsActive={filterIsActive}
        onChange={(e) => {
          checkListField.onChange(e);
          // @ts-expect-error - field types need to be fixed
          onChange(e.value);
        }}
        getCustomLabel={(items) => (items.length > 1 ? itemCountLabel(items.length) : items[0])}
        label={itemLabel}
        name={fieldName}
        showClearAll={showClearAll}
        trackingIdBase={trackingId}
        onApply={({ value, close }) => {
          pendo.track(trackingId + '::apply-event', {
            selected: value,
          });
          close();
        }}
        className={className}
      >
        {options.map((option) => (
          <DropdownFilterChipField.Option key={option.value} value={option.value} css={{ label: styles.truncate }}>
            {option.label}
          </DropdownFilterChipField.Option>
        ))}
      </DropdownFilterChipField>
    </div>
  );
};

export const SortMenu = ({
  options,
  selectedOption,
  onChange,
  containerCss,
  labelPrefix,
  trackingId,
}: {
  options: Option[];
  selectedOption: Option;
  labelPrefix?: string;
  onChange: (opt: string) => void;
  className?: string;
  containerCss?: Interpolation<Theme>;
  trackingId: string;
}) => {
  const [selectedItem, setSelectedItem] = useState(convertToItem(selectedOption));

  const _options = options.map((option) => ({ id: option.value, label: option.label }));

  return (
    <div css={containerCss}>
      <DropdownChip
        css={{
          maxWidth: 'unset',
          '.label': {
            fontWeight: 'bold',
          },
        }}
        trackingIdBase={trackingId}
        labelPrefix={labelPrefix}
        iconName='sort-descending'
        options={_options}
        selectedOption={selectedItem}
        onSelectOption={(item) => {
          onChange(item.id);
          setSelectedItem(item);
        }}
      />
    </div>
  );
};

const convertToItem = (option: Option) => {
  return { id: option.value, label: option.label };
};
